import { useState, type ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { LEVEL_BADGE_CLASSES, LEVEL_LABELS } from "@/lib/level-badge";
import type { Asset, AssetType } from "@/lib/assets";
import {
  getAssetHazardCells,
  getRiskDelta,
  getWorstRiskForHazardKey,
  formatIndicatorValue,
  hazardKeysFor,
  worstComputedRisk,
  HAZARD_LABELS,
  HORIZON_LABELS,
  type AssetHazardResult,
} from "@/lib/results";
import { getResultsForHorizon } from "@/lib/results-filters";
import { PendingScore } from "./PendingScore";
import { DroughtScores } from "./DroughtScores";
import { HazardStatusCells, RiskBadge, SeasonTag } from "./StatusCell";
import { assetTypeName, IMPORTANCE_ORDER } from "./asset-display";
import { HAZARD_TYPES, type ClimateScenario, type HazardType, type TimeHorizon } from "@shared/scoring/types";

/**
 * Sorts assets by their risk for one specific hazard, descending — the
 * hazard-axis question this view answers ("which assets are most exposed to
 * this hazard"). Assets with no computed risk for this hazard (pending,
 * no_data, not_applicable, or no row at all) sort after every computed one.
 * Changing Precipitation sorts by the worse of its two seasons (a sort
 * order only; both seasons' scores are still shown separately).
 * Ties (including all-non-computed groups) break by business importance
 * descending, then name ascending — see
 * docs/superpowers/specs/2026-09-15-tab3-by-hazard-view-design.md Decision 2.
 */
export function sortAssetsByRiskForHazard(
  assets: Asset[],
  hazardType: HazardType,
  resultsForOneHorizon: AssetHazardResult[],
): Asset[] {
  return [...assets].sort((a, b) => {
    const riskA = worstComputedRisk(getAssetHazardCells(a.id, hazardType, resultsForOneHorizon));
    const riskB = worstComputedRisk(getAssetHazardCells(b.id, hazardType, resultsForOneHorizon));

    if (riskA !== undefined || riskB !== undefined) {
      if (riskA === undefined) return 1; // no computed risk sorts after any computed risk
      if (riskB === undefined) return -1;
      if (riskA !== riskB) return riskB - riskA; // descending
    }

    const importanceDiff = IMPORTANCE_ORDER[a.businessImportance] - IMPORTANCE_ORDER[b.businessImportance];
    if (importanceDiff !== 0) return importanceDiff;
    return a.name.localeCompare(b.name);
  });
}

interface ByHazardViewProps {
  assets: Asset[];
  assetTypes: AssetType[];
  visibleHazards: HazardType[]; // from the shared Hazards multi-select filter, already resolved to "all 6" when empty — see AssessmentDetailedResults.tsx
  results: AssetHazardResult[]; // full, unsliced — every horizon x scenario combination
  scenario: ClimateScenario;
  activeHorizon: TimeHorizon;
  activeHorizons: TimeHorizon[]; // ordered, from the shared multi-select filter
  onHorizonChange: (horizon: TimeHorizon) => void;
}

export function ByHazardView({
  assets,
  assetTypes,
  visibleHazards,
  results,
  scenario,
  activeHorizon,
  activeHorizons,
  onHorizonChange,
}: ByHazardViewProps) {
  const orderedVisibleHazards = HAZARD_TYPES.filter((h) => visibleHazards.includes(h));
  const [selectedHazardId, setSelectedHazardId] = useState<HazardType | null>(orderedVisibleHazards[0] ?? null);
  const selectedHazard = orderedVisibleHazards.find((h) => h === selectedHazardId) ?? orderedVisibleHazards[0] ?? null;

  const resultsForActiveHorizon = getResultsForHorizon(results, activeHorizon, scenario);
  const resultsForHistorical = getResultsForHorizon(results, "historical", scenario);

  return (
    <div className="flex flex-col gap-3">
      {activeHorizons.length > 1 && (
        <div className="flex flex-col gap-1.5 self-end">
          <span className="text-xs font-medium text-muted-foreground">Showing horizon</span>
          <Select value={activeHorizon} onValueChange={(v) => onHorizonChange(v as TimeHorizon)}>
            <SelectTrigger className="h-7 w-28 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {activeHorizons.map((h) => (
                <SelectItem key={h} value={h}>
                  {HORIZON_LABELS[h]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_2fr]">
        <div className="flex flex-col gap-1 rounded-xl border border-border bg-card p-2">
          {orderedVisibleHazards.length === 0 && (
            <p className="p-4 text-sm text-muted-foreground">No hazards selected.</p>
          )}
          {orderedVisibleHazards.map((hazardType) => {
            const isSelected = hazardType === selectedHazard;
            // one badge per summary line: MAM, OND and/or "main season" for Changing Precipitation
            const worstRisks = hazardKeysFor([hazardType], resultsForActiveHorizon).map((key) => ({
              season: key.split(":")[1],
              risk: getWorstRiskForHazardKey(key, resultsForActiveHorizon),
            }));
            return (
              <button
                key={hazardType}
                type="button"
                aria-pressed={isSelected}
                onClick={() => setSelectedHazardId(hazardType)}
                className={cn(
                  "flex items-center justify-between gap-2 rounded-lg border p-3 text-left transition-colors",
                  isSelected ? "border-primary bg-primary/5" : "border-transparent hover:bg-muted",
                )}
              >
                <span className="text-sm font-bold">{HAZARD_LABELS[hazardType]}</span>
                <span className="flex flex-wrap items-center justify-end gap-2">
                  {worstRisks.map(({ season, risk }) => (
                    <span key={season ?? "all"} className="inline-flex items-center gap-1">
                      <SeasonTag season={season} />
                      {risk === null ? (
                        <PendingScore />
                      ) : (
                        <Badge className={LEVEL_BADGE_CLASSES[risk]}>{LEVEL_LABELS[risk]}</Badge>
                      )}
                    </span>
                  ))}
                </span>
              </button>
            );
          })}
        </div>

        {selectedHazard ? (
          <HazardAssetTable
            hazardType={selectedHazard}
            assets={assets}
            assetTypes={assetTypes}
            resultsForActiveHorizon={resultsForActiveHorizon}
            resultsForHistorical={resultsForHistorical}
          />
        ) : (
          <div className="flex items-center justify-center rounded-xl border border-border bg-card p-8 text-sm text-muted-foreground">
            Select a hazard to see its detail.
          </div>
        )}
      </div>
    </div>
  );
}

/** Per-season values on one line ("MAM … OND …") for Changing Precipitation;
 * a single value for every other hazard. */
function SeasonalValues({
  cells,
  render,
}: {
  cells: AssetHazardResult[];
  render: (cell: AssetHazardResult | undefined, index: number) => ReactNode;
}) {
  if (!cells.some((c) => c.season)) return <>{render(cells[0], 0)}</>;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
      {cells.map((c, i) => (
        <span key={c.season} className="inline-flex items-center gap-1">
          <SeasonTag season={c.season} />
          {render(c, i)}
        </span>
      ))}
    </span>
  );
}

function DeltaIndicator({ delta }: { delta: number | undefined }) {
  if (delta === undefined) return <span className="text-sm text-muted-foreground">—</span>;
  if (delta === 0) return <span className="text-sm text-muted-foreground">stable</span>;
  const isUp = delta > 0;
  return (
    <span className={cn("text-sm font-medium", isUp ? "text-red-600" : "text-emerald-700")}>
      {isUp ? "↑" : "↓"} {Math.abs(delta)}
    </span>
  );
}

function HazardAssetTable({
  hazardType,
  assets,
  assetTypes,
  resultsForActiveHorizon,
  resultsForHistorical,
}: {
  hazardType: HazardType;
  assets: Asset[];
  assetTypes: AssetType[];
  resultsForActiveHorizon: AssetHazardResult[];
  resultsForHistorical: AssetHazardResult[];
}) {
  const sortedAssets = sortAssetsByRiskForHazard(assets, hazardType, resultsForActiveHorizon);

  const rows = sortedAssets.map((asset) => {
    const cells = getAssetHazardCells(asset.id, hazardType, resultsForActiveHorizon);
    return {
      asset,
      cells,
      deltas: (cells.length ? cells : [undefined]).map((c) =>
        getRiskDelta(asset.id, hazardType, resultsForActiveHorizon, resultsForHistorical, c?.season),
      ),
    };
  });

  const scenarioCaveats = rows
    .flatMap((r) => r.cells.map((c) => c.scenarioCaveat))
    .filter((c): c is string => Boolean(c));
  const uniqueCaveats = Array.from(new Set(scenarioCaveats));

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6">
      <h2 className="text-lg font-bold">{HAZARD_LABELS[hazardType]}</h2>

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No assets match the current filters.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border text-xs font-bold uppercase tracking-[0.08em] text-slate-400">
                <th className="py-2 pr-3">Asset</th>
                <th className="py-2 pr-3">Exposure</th>
                <th className="py-2 pr-3">Vulnerability</th>
                <th className="py-2 pr-3">Risk</th>
                <th className="py-2 pr-3">Indicator</th>
                <th className="py-2">vs today</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ asset, cells, deltas }) => (
                <tr key={asset.id} className="border-b border-border/60 last:border-0">
                  <td className="py-2 pr-3">
                    <p className="font-medium">{asset.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {assetTypeName(asset, assetTypes)} · {asset.city}, {asset.country}
                    </p>
                  </td>
                  <td className="py-2 pr-3">
                    <HazardStatusCells
                      cells={cells}
                      renderComputed={(c) =>
                        c.exposure === undefined ? (
                          <PendingScore />
                        ) : (
                          <Badge className={LEVEL_BADGE_CLASSES[c.exposure]}>{c.exposure}</Badge>
                        )
                      }
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <HazardStatusCells
                      cells={cells}
                      renderComputed={(c) =>
                        c.vulnerability === undefined ? (
                          <PendingScore />
                        ) : (
                          <Badge className={LEVEL_BADGE_CLASSES[c.vulnerability]}>{c.vulnerability}</Badge>
                        )
                      }
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <HazardStatusCells cells={cells} renderComputed={(c) => <RiskBadge cell={c} />} />
                  </td>
                  <td className="py-2 pr-3 text-muted-foreground">
                    <SeasonalValues
                      cells={cells}
                      render={(c) =>
                        hazardType === "drought" && c?.droughtDetail ? (
                          <DroughtScores detail={c.droughtDetail} isFuture={c.horizon !== "historical"} />
                        ) : (
                          formatIndicatorValue(hazardType, c?.indicatorValue) ?? "—"
                        )
                      }
                    />
                  </td>
                  <td className="py-2">
                    <SeasonalValues cells={cells} render={(_, i) => <DeltaIndicator delta={deltas[i]} />} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="border-t border-border pt-3 text-xs text-muted-foreground">
        {/* Importance IS a scoring input: it feeds Vulnerability via
            combineVulnerability() in shared/scoring/vulnerability.ts, per
            functional spec §2.2. Kept in sync with ByAssetView's footer. */}
        <p>
          Asset importance is one of the two inputs to vulnerability, alongside the physical impact typical
          for the asset type.
        </p>
        {uniqueCaveats.map((caveat) => (
          <p key={caveat} className="mt-1">{caveat}</p>
        ))}
      </div>
    </div>
  );
}
